/**
 * BaseAudioSource
 *
 * 定義所有 audio source（mic / file / future）的共同介面。
 * 這個 class 不負責任何實際音訊處理，只負責：
 *  - lifecycle 定義
 *  - chunk callback 管理
 *  - 狀態保護
 */

export class BaseAudioSource {
  constructor() {
    // callback: function(Float32Array)
    this._onChunkCallback = null;

    // lifecycle state
    this._initialized = false;
    this._running = false;
  }

  /**
   * 初始化 audio source
   * @param {Object} config - source-specific config
   */
  init(config = {}) {
    this._initialized = true;
  }

  /**
   * 開始產生 audio chunk
   */
  start() {
    if (!this._initialized) {
      throw new Error("AudioSource must be initialized before start()");
    }
    this._running = true;
  }

  /**
   * 暫停產生 audio chunk（不一定釋放資源）
   */
  pause() {
    this._running = false;
  }

  /**
   * 停止 audio source，並重置狀態
   */
  stop() {
    this._running = false;
  }

  /**
   * 銷毀 audio source（釋放所有資源）
   */
  destroy() {
    this._running = false;
    this._initialized = false;
    this._onChunkCallback = null;
  }

  /**
   * 註冊 audio chunk callback
   * @param {Function} callback - function(Float32Array chunk)
   */
  onChunk(callback) {
    if (typeof callback !== "function") {
      throw new Error("onChunk callback must be a function");
    }
    this._onChunkCallback = callback;
  }

  /**
   * 子類別呼叫，用來送出 audio chunk
   * @protected
   */
  _emitChunk(chunk, metadata = null) {
    if (!this._onChunkCallback) return false;
    // 呼叫端必須明確回傳是否已接受 chunk；File Mode 收尾會據此決定能否
    // 推進 resume offset，避免 transport 拒絕時把未送音訊誤標成已送。
    return this._onChunkCallback(chunk, metadata);
  }

  /**
 * Utility: check initialized state
 */
  isInitialized() {
    return this._initialized;
  }

  /**
   * Utility: check running state
   */
  isRunning() {
    return this._running;
  }
}
